/**
 * GENERAL NOTES:
 *
 * This is our main Uploader widget - UploadButton, paired with the UploadErrors, UploadAttachments. These components are all wired together
 * through redux, which sometimes leads to complications. The redux store has a key system so that you can have multiple attachment uploaders
 * on one page.
 *
 * ISSUES & COMPLICATIONS:
 * - Many different types of attachments, leads to a lot of different use cases & parameters
 * - Redux store system can be a headache.
 *   - Preloading saved attachments can be a little complicated (have to load into the redux state with an appropriate key).
 * - It feels too bundled together and trying to do too much, but it is complicated with the variety of use cases.
 */
import React from "react"

import {connect} from 'react-redux'
import './reducers'
import {postUpload} from './http'
import {parseResponseError} from 'js/studios/shared/http'
import {trackEventSP} from 'js/studios/shared/tracking'
import {Upload, sortUploads} from 'js/studios/shared/attachments'

@connect()
export class UploadAttachment extends React.Component {

  constructor(props){
    super(props)

    this.state = {
      percent: 0,
      completed: false,
      error: "",
      srcUrl: "",
      status: "",
      uploading: true,
      isImage: this.props.upload.isImage,
    }

    this.request = null

    if (this.state.isImage){
      var reader = new FileReader()
      reader.addEventListener("load", function () {
        this.setState({srcUrl: reader.result})
      }.bind(this), false)
      reader.readAsDataURL(this.props.upload.file)
    }

    // validate file
    let error = ''
    if (this.props.upload.file.size > 10 * Math.pow(props.maxFileSize || 2, 20)){
      error = 'This file is too large'
    }

    if (error.length){
      this.state.error = error
    }
  }

  progress(e){
    if(e.lengthComputable){
      let percent = (e.loaded * 100)/e.total
      if (percent < 100){
        this.setState({percent: percent})
      }
    }
  }

  startUpload(){
    var progressCallback = this.progress.bind(this)
    this.setState({
      percent: 1 // shows we're starting to upload
    })

    // submit request
    this.request = postUpload(this.props.upload.file, progressCallback)
    this.request.done(
      (response) => {
        this.setState({percent: 100})
          this.props.dispatch({
            type: "SUCCESSFUL_ATTACHMENT",
            key: this.props.upload.key,
            data: response
          })
          trackEventSP('action_add_attachment', 'consultation', {
            'status': 'Success',
            'response': ''
          })
          if (this.props.onUpload){
            this.props.onUpload(this.props.upload, response)
          }
      }
    ).fail(
      (response) => {
        let error = parseResponseError(response)
        this.setState({error: error})
        this.props.dispatch({
          type: "FAILED_ATTACHMENT",
          key: this.props.upload.key,
          error: error
        })
        trackEventSP('action_add_attachment', 'consultation', {
          'status': 'Failure',
          'response': response.responseText
        })
      }
    )
  }

  remove(){
    this.props.dispatch({
      type: "REMOVE_UPLOAD",
      key: this.props.upload.key
    })

    if (this.props.onRemove){
      this.props.onRemove(this.props.upload)
    }
  }

  removeUploader(){
    this.setState({
      uploading: false
    })
  }

  componentDidUpdate(){
    if (this.state.uploading && this.state.percent === 100){
      setTimeout(this.removeUploader.bind(this), 100)
    }
  }

  componentDidMount(){
    if (this.state.error.length === 0){
      this.startUpload()
    } else {
      this.props.dispatch({
        type: "FAILED_ATTACHMENT",
        key: this.props.upload.key,
        error: this.state.error
      })
    }
  }

  componentWillUnmount(){
    if (this.request){
      this.request.abort()
    }
  }

  render(){
    return (
      <Upload
        className={this.props.className}
        tagName={this.props.tagName}
        inError={this.state.error.length > 0}
        uploading={this.state.uploading}
        percent={this.state.percent}
        srcUrl={this.state.srcUrl}
        isImage={this.state.isImage}
        onRemove={this.remove.bind(this)}
        index={this.props.index}
        upload={this.props.upload}
        readOnly={this.props.readOnly}
        />
    )
  }
}


@connect()
export class SavedUploadAttachment extends React.Component {
  remove(){
    this.props.dispatch({
      type: "REMOVE_UPLOAD",
      key: this.props.upload.key
    })

    if (this.props.onRemove){
      this.props.onRemove(this.props.upload)
    }
  }

  render(){
    let upload = this.props.upload
    return (
      <Upload
        className={this.props.className}
        inError={false}
        uploading={false}
        percent={100}
        srcUrl={upload.data ? upload.data.fullUrl : upload.fullUrl}
        isImage={upload.data ? upload.data.isImage : upload.isImage}
        onRemove={this.remove.bind(this)}
        index={this.props.index}
        upload={upload}
        readOnly={this.props.readOnly}
        overlayBuilder={this.props.overlayBuilder}
        >
        {this.props.children}
      </Upload>
    )
  }
}

/**
 * Display the attachments, filtering down by an 'uploaderKey' if provided. While the uploads are uploading, they are displayed
 * by the UploadAttachment component, but switched to SavedUploadAttachment once it's completed (that class is overridable by savedUploadClass prop)
 */
@connect(state => ({uploadsWidget: state.uploadsWidget}))
export class UploadAttachments extends React.Component {
  shiftAttachmentIndex(upload, delta, ev) {
    ev.preventDefault()
    this.props.dispatch({ type: "MOVE_UPLOAD", upload: upload, delta: delta })
  }

  buildOrderableOverlay(upload) {
    const idx = this.props.uploadsWidget.uploads.indexOf(upload)

    let overlay = null
    if (upload.data.videoUrl) {
     overlay = <div className="attachment-overlay__action-row">
        <a data-fancybox data-fancybox-type="iframe" className="attachment-overlay__action attachment-overlay__action__video" title="View attachment" href={upload.data.videoUrl}>&#x25B6;</a>
        <img src={upload.smallUrl} />
      </div>
    } else if (upload.isImage) {
      overlay = <div className="attachment-overlay__action-row">
        <a data-fancybox className="attachment-overlay__action" title="View attachment" href={upload.data.fullUrl}>&#xe816;</a>
      </div>
    }


    return <div className="attachment-overlay">
      {overlay}
      <div className="attachment-overlay__action-row">
        {idx > 0 &&
          <a className="attachment-overlay__action" title="Move attachment left" href="#" onClick={this.shiftAttachmentIndex.bind(this,upload,-1)}>&#59229;</a>
        }
        {idx < this.props.uploadsWidget.uploads.length - 1 &&
          <a className="attachment-overlay__action" title="Move attachment right" href="#" onClick={this.shiftAttachmentIndex.bind(this,upload,1)}>&#59230;</a>
        }
      </div>
    </div>
  }

  render(){
    var uploads = this.props.uploadsWidget.uploads.filter(upload => upload.uploaderKey === this.props.uploaderKey)
    if (!this.props.orderable) {
      uploads.sort(sortUploads)
    }

    var SavedUploadClass = this.props.savedUploadClass || SavedUploadAttachment

    var attachments = uploads.map(function(upload, index){
      if (upload.data && upload.data.id){
        let overlayBuilder = this.props.orderable ? this.buildOrderableOverlay.bind(this) : undefined
        return <SavedUploadClass key={upload.key} upload={upload} index={index + 1} readOnly={this.props.readOnly} onRemove={this.props.onRemove} extraProps={this.props.extraProps} overlayBuilder={overlayBuilder} />
      }
      return <UploadAttachment key={upload.key} upload={upload} maxFileSize={this.props.maxFileSize} index={index + 1} onRemove={this.props.onRemove} onUpload={this.props.onUpload} extraProps={this.props.extraProps} />
    }, this)

    var className = this.props.className ? this.props.className : ""
    if (attachments.length === 0) {
      className += ' empty'
    }

    return (
      <ul className={className}>
        {attachments}
      </ul>
    )
  }
}

@connect(state => ({uploadsWidget: state.uploadsWidget}))
export class UploadErrors extends React.Component {
  render(){
    var uploads = this.props.uploadsWidget.uploads.filter(upload => upload.uploaderKey === this.props.uploaderKey)
    uploads.sort(sortUploads)
    uploads = uploads.filter(upload => upload.error && upload.error.length > 0)
    if (uploads.length === 0){
      return null
    }
    return (
      <div className={"upload-errors " + (this.props.className ? this.props.className : "")}>
        {uploads.map(upload => (
          <div className="upload-error" key={upload.key}>{upload.file.name}: {upload.error}</div>
        ))}
      </div>
    )
  }
}

/**
 * Main button to trigger uploads. Specify an 'uploaderKey' if you want to put your attachments into a particular store (be sure to use the same key for UploadAttachments).
 * Handlers:
 *   onUploadClick -> (jQuery event) : triggers when the upload button is clicked
 *   onCreateUpload -> (uploadedFile, uploadFileKey) : triggers when the upload has been selected and has begun uploading
 */
@connect()
export class UploadButton extends React.Component {

  constructor(props){
    super(props)
    this.uploadClickTimer = null
  }

  clickUploadInput(e){
    // I only allow the click handler to fire every 750 milleseconds as i hit issues with react
    // firing the handler twice. It only does this if you're clicking a react element from another react element, so i'm
    // assuming react has some form of event bubbling that's separate from the DOM event bubbling. It's odd and confusing
    // and I couldn't find much mention of it online... but this fixes the issue.
    if(e.target.name !== 'attachments'){
      e.preventDefault()
      if (!this.uploadClickTimer || window.Date.now() - this.uploadClickTimer > 750){
        this.uploadClickTimer = window.Date.now()
        if (this.props.onUploadClick){
          this.props.onUploadClick(e)
        }
        this.attachmentInput.click()
      }
    }
  }

  onChange(e){
    var uploadedFiles = [], files = e.target.files
    for (let i = 0; i < files.length; i++){
      let file = files.item(i)
      uploadedFiles.push(file)
    }

    this.props.dispatch({
      type: "ADD_UPLOADS",
      uploadedFiles: uploadedFiles,
      uploaderKey: this.props.uploaderKey
    })

    if (this.props.onCreateUpload){
      uploadedFiles.forEach((uploadedFile) => {
        let uploadFileKey = uploadedFile.name + uploadedFile.size
        this.props.onCreateUpload(uploadedFile, uploadFileKey)
      })
    }
  }

  render(){
    let multiple = this.props.multiple !== undefined ? this.props.multiple : true
    return (
      <span className={this.props.wrapperClassName ? this.props.wrapperClassName : ''} onClick={this.clickUploadInput.bind(this)} data-track-event-sp="click_add_attachment*consultation">
        {this.props.overrideContent && this.props.children}
        {!this.props.overrideContent && (
          <button type="button" className={this.props.className}>
            {this.props.children}
          </button>
        )}
        <input type="file" ref={node => this.attachmentInput = node} name="attachments" aria-label="Add attachments" style={{display:"none", height: 0, width: 0}} multiple={multiple} onChange={this.onChange.bind(this)} />
      </span>
    )
  }
}
